import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';

import LoadingSpinner from '../partials/LoadingSpinner';
import WarehouseNav from './WarehouseNav';
import WarehouseLogin from './WarehouseLogin';
import WarehouseProfile from './WarehouseProfile';
import PickingBatches from './screens/PickingBatches';
import PickBatch from './screens/PickBatch';
import PackingBatches from './screens/PackingBatches';
import PackBatch from './screens/PackBatch';
import PackShipment from './screens/PackShipment';
import ReturnBatches from './screens/ReturnBatches';
import ReturnBatch from './screens/ReturnBatch';

import { warehouseStore } from '../../warehouse/store';
import { setWarehouseNavigate } from '../../warehouse/utils/navigation';
import { getSessionToken, getSessionProfile } from '../../warehouse/utils/session';
import { homeRouteForPermissions } from '../../warehouse/utils/permissions';

const EMPLOYEE_LOGIN_KEY = 'employeeLogin';

// Mirrors the RN app's index screen: a stored employeeLogin means the
// session was established - resume at the first workflow the user's
// permissions allow. Otherwise go to login.
const WarehouseIndex = () => {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const employeeLogin = localStorage.getItem(EMPLOYEE_LOGIN_KEY);
        if (employeeLogin) {
          const profile = await getSessionProfile();
          setTarget(homeRouteForPermissions(profile?.permissions));
        } else {
          setTarget('/warehouse/app/login');
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        setTarget('/warehouse/app/login');
      }
    };
    checkAuthStatus();
  }, []);

  if (!target) {
    return <div className="warehouse-loading"><LoadingSpinner statusText="Loading" /></div>;
  }

  return <Navigate to={target} replace />;
};

// Gate + chrome for every authed warehouse screen: no stored session
// token means a deep link without a login, so redirect to login. The
// warehouse nav bar replaces the admin PageHeader nav on these pages.
const WarehouseLayout = () => {
  const [authState, setAuthState] = useState('checking');

  useEffect(() => {
    getSessionToken()
      .then(token => setAuthState(token ? 'authed' : 'anon'))
      .catch(() => setAuthState('anon'));
  }, []);

  if (authState === 'checking') {
    return <div className="warehouse-loading"><LoadingSpinner statusText="Loading" /></div>;
  }

  if (authState === 'anon') {
    return <Navigate to="/warehouse/app/login" replace />;
  }

  return (
    <div className="warehouse-shell">
      <WarehouseNav />
      <main className="warehouse-container">
        <Outlet />
      </main>
    </div>
  );
};

const WarehouseApp = () => {
  const navigate = useNavigate();

  // The api slice's 401 handler lives outside React - give it the SPA
  // navigate function while this section is mounted
  useEffect(() => {
    setWarehouseNavigate(navigate);
    return () => setWarehouseNavigate(null);
  }, [navigate]);

  return (
    <Provider store={warehouseStore}>
      <Routes>
        <Route index element={<WarehouseIndex />} />
        <Route path="login" element={<WarehouseLogin />} />
        <Route element={<WarehouseLayout />}>
          <Route path="picking" element={<PickingBatches />} />
          <Route path="picking/:batchId" element={<PickBatch />} />
          <Route path="packing" element={<PackingBatches />} />
          <Route path="packing/:batchId" element={<PackBatch />} />
          <Route path="packing/:batchId/shipment/:orderId/:shipmentId" element={<PackShipment />} />
          <Route path="returns" element={<ReturnBatches />} />
          <Route path="returns/:batchId" element={<ReturnBatch />} />
          <Route path="profile" element={<WarehouseProfile />} />
        </Route>
        <Route path="*" element={<Navigate to="/warehouse/app" replace />} />
      </Routes>
    </Provider>
  );
};

export default WarehouseApp;
